/**
 * The SpecTree MCP tool list (epic PI-SPEC-SW-E316, feature PI-SPEC-SW-1662).
 *
 * Every tool the SpecTree MCP server (`packages/mcp`) can register, under any
 * tool profile, as its Claude wire name: the `spectree__` source literal with
 * the prefix stripped (`resolveToolName` with `SPECTREE_TOOL_PREFIX` unset),
 * and the `fogbugz_*` tools unchanged. Claude Code exposes each one as
 * `mcp__spectree__<name>` ({@link SPECTREE_MCP_TOOLS}).
 *
 * Why it is here: `--allowedTools` does not revoke the `mcp__spectree__*`
 * grants a person saved in their own Claude Code settings, so a Toolbox engine
 * that only allows tools could still reach a SpecTree write tool through such
 * a grant. Every Toolbox engine therefore also denies, by name,
 * {@link spectreeToolsDeniedFor}(its allowlist).
 *
 * The list only grows: SpecTree never renames or removes an MCP tool (the
 * Copilot freeze rule). A SpecTree test
 * (`packages/mcp/tests/toolbox-contract-tool-list.test.ts`) fails whenever the
 * MCP server registers a tool this list does not name, or this list names a
 * tool no profile registers: adding an MCP tool means adding it here.
 *
 * Depends on nothing. No secrets, no service URLs.
 */
/** The `.mcp.json` key the SpecTree MCP server is registered under. */
export const SPECTREE_MCP_SERVER = "spectree";
/** The prefix Claude Code puts before every SpecTree MCP tool name. */
export const SPECTREE_MCP_TOOL_PREFIX = `mcp__${SPECTREE_MCP_SERVER}__`;
/**
 * Every SpecTree MCP tool, as its bare wire name, sorted. Includes the
 * deprecated single-purpose tools and the shared-token `fogbugz_*` tools.
 */
export const SPECTREE_MCP_TOOL_NAMES = Object.freeze([
    "add_acceptance_criterion",
    "add_external_link",
    "add_validation",
    "ado_get_work_item",
    "ado_list_estimated_items",
    "ado_list_my_items",
    "ado_list_product_areas",
    "ado_save_grooming_brief",
    "ado_save_product_areas_draft",
    "append_ai_note",
    "audit_epic",
    "audit_epic_request",
    "comment_on_epic_request",
    "complete_task_with_validation",
    "complete_work",
    "compliance_dashboard",
    "create_epic",
    "create_epic_complete",
    "create_epic_request",
    "create_feature",
    "create_from_template",
    "create_personal_project",
    "create_task",
    "create_team_child",
    "delete_epic_request",
    "delete_feature",
    "delete_task",
    "draft_epic_request",
    "end_session",
    "fogbugz_assign_case",
    "fogbugz_create_case",
    "fogbugz_create_project",
    "fogbugz_get_attachment",
    "fogbugz_get_case",
    "fogbugz_get_case_link",
    "fogbugz_get_evtx_digest",
    "fogbugz_list_my_cases",
    "fogbugz_list_people",
    "fogbugz_list_projects",
    "fogbugz_list_statuses",
    "fogbugz_read_case",
    "fogbugz_read_evtx_digest",
    "fogbugz_read_search",
    "fogbugz_search_cases",
    "fogbugz_update_case",
    "get_active_session",
    "get_ai_context",
    "get_blocked_summary",
    "get_changelog",
    "get_code_context",
    "get_decision_context",
    "get_epic",
    "get_epic_request",
    "get_execution_plan",
    "get_feature",
    "get_instructions",
    "get_last_session",
    "get_my_work",
    "get_next_required_action",
    "get_pack_manifest",
    "get_personal_scope",
    "get_progress_summary",
    "get_server_info",
    "get_session_history",
    "get_session_state",
    "get_status",
    "get_structured_description",
    "get_task",
    "get_team_path",
    "get_template",
    "link_branch",
    "link_code_file",
    "link_commit",
    "link_file",
    "link_function",
    "link_pr",
    "list_decisions",
    "list_epic_owner_teams",
    "list_epic_request_comments",
    "list_epic_requests",
    "list_epics",
    "list_features",
    "list_personal_epic_requests",
    "list_personal_projects",
    "list_personal_statuses",
    "list_statuses",
    "list_tasks",
    "list_teams",
    "list_teams_tree",
    "list_templates",
    "list_validations",
    "log_decision",
    "log_progress",
    "manage_ai_context",
    "manage_code_context",
    "manage_dependencies",
    "manage_description",
    "manage_epic_requests",
    "manage_progress",
    "manage_skill_packs",
    "manage_validations",
    "mark_blocked",
    "mark_docs_audit_complete",
    "mark_manual_validated",
    "mark_unblocked",
    "move_team",
    "preview_template",
    "react_to_epic_request",
    "record_feature_model",
    "record_model_used",
    "remove_validation",
    "reorder_epic",
    "reorder_feature",
    "reorder_item",
    "reorder_task",
    "report_blocker",
    "reset_validations",
    "run_all_validations",
    "run_validation",
    "save_as_template",
    "search",
    "search_decisions",
    "set_ai_context",
    "set_execution_metadata",
    "set_model_recommendation",
    "set_structured_description",
    "start_session",
    "start_work",
    "sync_local_packs",
    "transfer_epic_request_scope",
    "transfer_epic_scope",
    "unlink_code_file",
    "update_epic",
    "update_epic_request",
    "update_feature",
    "update_planning_progress",
    "update_section",
    "update_task",
]);
/** Every SpecTree MCP tool as Claude Code names it: `mcp__spectree__<name>`, in {@link SPECTREE_MCP_TOOL_NAMES} order. */
export const SPECTREE_MCP_TOOLS = Object.freeze(SPECTREE_MCP_TOOL_NAMES.map((name) => `${SPECTREE_MCP_TOOL_PREFIX}${name}`));
/** True when `name` is one of {@link SPECTREE_MCP_TOOLS} (the full `mcp__spectree__<name>` form). */
export function isSpectreeMcpTool(name) {
    return SPECTREE_MCP_TOOLS.includes(name);
}
/**
 * Every {@link SPECTREE_MCP_TOOLS} entry not in `allowed`, in list order: the
 * names a Toolbox engine passes to `--disallowedTools` so a saved
 * `mcp__spectree__*` grant cannot reach a tool off its allowlist. Entries of
 * `allowed` that are not SpecTree tools (`Read`, `Grep`, `Skill`, ...) are
 * ignored.
 */
export function spectreeToolsDeniedFor(allowed) {
    const allowedSet = new Set(allowed);
    return SPECTREE_MCP_TOOLS.filter((tool) => !allowedSet.has(tool));
}
//# sourceMappingURL=spectree-tools.js.map